export type Platform =
  | 'archidekt'
  | 'moxfield'
  | 'manabox'
  | 'deckcheck'
  | 'commandersalt'
  | 'hareruya'
  | 'slinglab'
  | 'edhrec'
  | 'tappedout'
  | 'deckstats'
  | 'unknown'
  | 'none'

export interface DetectedUrl {
  platform: Platform
  url: string
  deckId?: string
}

const PATTERNS: { platform: Platform; regex: RegExp; idGroup: number }[] = [
  { platform: 'archidekt', regex: /https?:\/\/(?:www\.)?archidekt\.com\/decks\/(\d+)/i, idGroup: 1 },
  { platform: 'moxfield', regex: /https?:\/\/(?:www\.)?moxfield\.com\/decks\/([\w-]+)/i, idGroup: 1 },
  { platform: 'manabox', regex: /https?:\/\/(?:www\.)?manabox\.app\/decks\/([\w-]+)/i, idGroup: 1 },
  // The three patterns below are ANCHORED (^), unlike the older ones above:
  // an unanchored host pattern also matches a hostile URL that merely carries
  // the host name in its query string. The older patterns are left alone here
  // on purpose - changing them is a separate task with its own regressions.
  { platform: 'deckcheck', regex: /^https?:\/\/(?:www\.)?deckcheck\.co\/app\/(?:decklist|deckview|builder)\/([\w-]+)/i, idGroup: 1 },
  { platform: 'commandersalt', regex: /^https?:\/\/(?:www\.)?commandersalt\.com\/details\/deck\/([0-9a-f]{32})/i, idGroup: 1 },
  // A SlingLab playtest link carries the decklist itself in the query string
  // (?deck= or ?list=), so there is no id to capture - idGroup 0 means "no
  // deckId". A ?deckId= link IS matched here and refused by the API, which
  // answers with a 400 explaining that it needs a SlingLab login.
  { platform: 'slinglab', regex: /^https?:\/\/(?:www\.)?slinglab\.gg\/playtest(?:\/)?\?/i, idGroup: 0 },
  // A public SlingLab deck page. The API fetches it and reads the deck's live
  // card list. The uuid is anchored at its end so `/decks/<uuid>.evil` or
  // `/decks/<uuid>/other` is not a deck page. Still idGroup 0: SlingLab stays
  // out of LINKABLE_PLATFORMS, so there is no id to keep.
  { platform: 'slinglab', regex: /^https?:\/\/(?:www\.)?slinglab\.gg\/decks\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/?(?:[?#]|$)/i, idGroup: 0 },
  { platform: 'tappedout', regex: /https?:\/\/(?:www\.)?tappedout\.net\//i, idGroup: 0 },
  { platform: 'deckstats', regex: /https?:\/\/(?:www\.)?deckstats\.net\//i, idGroup: 0 },
]

/**
 * The deckpreview hash from an EDHREC deck URL, or null if the string is not
 * one. Exported for tests; `detectUrl` is the only production caller.
 *
 * An EDHREC deckpreview is a FROZEN SNAPSHOT taken when the deck was
 * submitted, not a live mirror of the original Moxfield/Archidekt deck it came
 * from, so a "Refresh from EDHREC" always returns the same cards. That is the
 * same contract as any list a user re-exports and re-pastes, so it stays
 * linkable.
 */
export function edhrecDeckPreviewId(candidate: string): string | null {
  try {
    const parsed = new URL(candidate)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
    const hostname = parsed.hostname.toLowerCase().replace(/^www\./, '')
    if (hostname !== 'edhrec.com') return null
    // Reject credentials in the authority and a non-default port, matching
    // the API's own EDHREC URL validation - the API is authoritative and
    // would refuse these anyway, but a client-side gate that accepted them
    // would still send `https://user:pass@edhrec.com/...` onward as a
    // "fetchable" URL, and (for a linkable platform) persist it as a saved
    // deck's source_url.
    if (parsed.username || parsed.password) return null
    const defaultPort = parsed.protocol === 'http:' ? '80' : '443'
    if (parsed.port && parsed.port !== defaultPort) return null
    // Anchored at BOTH ends against the pathname alone (the query string is
    // not part of `pathname`, so `?utm_source=x` is ignored for free). The
    // trailing `$` is what rejects `<id>.evil` and `<id>/other-path`, and the
    // `+` is what rejects an empty id.
    const match = parsed.pathname.match(/^\/deckpreview\/([\w-]+)\/?$/)
    return match ? match[1] : null
  } catch {
    return null
  }
}

/** The hosts a Hareruya deck link may live on. Exact match, never a substring. */
const HARERUYA_HOSTS: ReadonlySet<string> = new Set([
  'hareruyamtg.com',
  'www.hareruyamtg.com',
  'deck.hareruyamtg.com',
])

/**
 * The deck id from a Hareruya deck URL, or null if the string is not one.
 * Exported for tests; `detectUrl` is the only production caller.
 *
 * Matched STRUCTURALLY like EDHREC (parsed host + anchored pathname), so a
 * lookalike host (`hareruyamtg.com.evil.example`) or a hostile URL that only
 * carries the host in its query string is never a Hareruya deck. Accepted
 * shapes:
 *   - www (or apex): `/<2-letter locale>/deck/<digits>/show/`
 *   - www (or apex): `/decks/list/<digits>` and `/decks/<digits>`
 *   - deck.hareruyamtg.com: `/list/<digits>` and `/<digits>`
 * A trailing slash is optional everywhere. The query string is not part of
 * `pathname`, so `?display_token=...` is ignored here, and `detectUrl` keeps
 * the original URL (token included) for the API, which needs it for a deck
 * shared by token. Every other Hareruya path (store pages, deck search
 * results, metagame pages) is not a deck link.
 */
export function hareruyaDeckId(candidate: string): string | null {
  try {
    const parsed = new URL(candidate)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
    const hostname = parsed.hostname.toLowerCase()
    if (!HARERUYA_HOSTS.has(hostname)) return null
    // Same authority hygiene as edhrecDeckPreviewId: no credentials, no
    // non-default port, so such a URL is never sent on or saved as a source.
    if (parsed.username || parsed.password) return null
    const defaultPort = parsed.protocol === 'http:' ? '80' : '443'
    if (parsed.port && parsed.port !== defaultPort) return null
    const path = parsed.pathname
    const patterns = hostname === 'deck.hareruyamtg.com'
      ? [/^\/list\/(\d+)\/?$/, /^\/(\d+)\/?$/]
      : [/^\/[a-z]{2}\/deck\/(\d+)\/show\/?$/i, /^\/decks\/list\/(\d+)\/?$/, /^\/decks\/(\d+)\/?$/]
    for (const re of patterns) {
      const match = path.match(re)
      if (match) return match[1]
    }
    return null
  } catch {
    return null
  }
}

export function detectUrl(text: string): DetectedUrl {
  const trimmed = text.trim()

  // Check if the input looks like a URL (first line is a URL)
  const firstLine = trimmed.split('\n')[0].trim()
  if (!firstLine.match(/^https?:\/\//i)) {
    // eslint-disable-next-line i18next/no-literal-string -- platform code
    return { platform: 'none', url: '' }
  }

  // EDHREC is matched STRUCTURALLY, not by a PATTERNS regex. A deckpreview id
  // is an opaque `[\w-]+` hash, so a substring/prefix regex over the whole URL
  // would also accept `/deckpreview/realId.evil`,
  // `/deckpreview/realId/other-path` or a spoofed host that merely contains
  // "edhrec.com". Parsing with `URL` and matching an anchored PATHNAME gets
  // those boundaries right by construction - the same precedent
  // `lib/archidektLink.ts`'s archidektDeckIdFromUrl already sets.
  const edhrecId = edhrecDeckPreviewId(firstLine)
  if (edhrecId) {
    // eslint-disable-next-line i18next/no-literal-string -- platform code
    return { platform: 'edhrec', url: firstLine, deckId: edhrecId }
  }

  // Hareruya, structurally for the same reasons as EDHREC above.
  const hareruyaId = hareruyaDeckId(firstLine)
  if (hareruyaId) {
    // eslint-disable-next-line i18next/no-literal-string -- platform code
    return { platform: 'hareruya', url: firstLine, deckId: hareruyaId }
  }

  for (const { platform, regex, idGroup } of PATTERNS) {
    const match = firstLine.match(regex)
    if (match) {
      return {
        platform,
        url: firstLine,
        deckId: idGroup > 0 ? match[idGroup] : undefined,
      }
    }
  }

  // eslint-disable-next-line i18next/no-literal-string -- platform code
  return { platform: 'unknown', url: firstLine }
}

export const PLATFORM_NAMES: Record<Platform, string> = {
  archidekt: 'Archidekt',
  moxfield: 'Moxfield',
  manabox: 'ManaBox',
  deckcheck: 'DeckCheck',
  commandersalt: 'CommanderSalt',
  hareruya: 'Hareruya',
  slinglab: 'SlingLab',
  edhrec: 'EDHREC',
  tappedout: 'TappedOut',
  deckstats: 'Deckstats',
  unknown: 'Unknown',
  none: '',
}

/**
 * The fetchable hosts in the order the UI names them (Moxfield first: it is
 * the host most links come from). DeckEntry's helper line, the fetchable set
 * and the supported-links copy are all rendered from this list, so none of
 * them can advertise a host the fetch rule does not accept, or the reverse.
 */
export const FETCHABLE_PLATFORM_ORDER: readonly Platform[] = [
  'moxfield', 'archidekt', 'manabox', 'deckcheck', 'commandersalt', 'hareruya', 'edhrec', 'slinglab',
]

/**
 * The deck hosts `POST /decks/fetch-url` can resolve to decklist text. Every
 * surface that offers a one-step "paste a link and we fetch it" flow tests
 * membership here rather than spelling out the platforms inline, so adding a
 * host is one edit instead of five. DERIVED from the order above.
 */
export const FETCHABLE_PLATFORMS: ReadonlySet<Platform> = new Set<Platform>(FETCHABLE_PLATFORM_ORDER)

export function isFetchablePlatform(platform: Platform): boolean {
  return FETCHABLE_PLATFORMS.has(platform)
}

/**
 * The fetchable hosts a saved deck may keep as a SOURCE LINK, so it can be
 * refreshed later. That is the fetchable set minus SlingLab: a SlingLab
 * playtest URL encodes the decklist itself, so the link is the deck rather
 * than a pointer at a hosted page. There is nothing to re-read and nothing to
 * "Refresh from", so we fetch those decks but never link them.
 */
export const LINKABLE_PLATFORMS: ReadonlySet<Platform> = new Set<Platform>(
  [...FETCHABLE_PLATFORMS].filter((p) => p !== 'slinglab'),
)

export function isLinkablePlatform(platform: Platform): boolean {
  return LINKABLE_PLATFORMS.has(platform)
}

/**
 * The ONE copy string naming the supported deck hosts. Every error and status
 * message that lists them reads this, so the list cannot drift between
 * surfaces the way it did when each component spelled out its own.
 */
export const SUPPORTED_DECK_LINKS_COPY =
  `${FETCHABLE_PLATFORM_ORDER.filter(p => p !== 'slinglab').map(p => PLATFORM_NAMES[p]).join(', ')}, or a SlingLab deck or playtest link`

export interface ExportGuide {
  platform: string
  steps: string[]
}

export const EXPORT_GUIDES: Partial<Record<Platform, ExportGuide>> = {
  moxfield: {
    platform: 'Moxfield',
    steps: [
      'Open your deck on Moxfield',
      'Click the three dots menu (...) at the top right',
      'Select "Export" then choose "Text" format (not Arena, which omits non-Arena cards)',
      'Click "Copy" and paste the text here',
    ],
  },
  manabox: {
    platform: 'ManaBox',
    steps: [
      'Open your deck in ManaBox',
      'Tap the Share icon',
      'Select "Export Decklist"',
      'Copy to clipboard and paste here',
    ],
  },
  tappedout: {
    platform: 'TappedOut',
    steps: [
      'Open your deck on TappedOut',
      'Click "Export" near the top of the page',
      'Choose "Text" format',
      'Copy the text and paste it here',
    ],
  },
  deckstats: {
    platform: 'Deckstats',
    steps: [
      'Open your deck on Deckstats',
      'Click "Export / Print"',
      'Choose "Copy to Clipboard"',
      'Paste the text here',
    ],
  },
  unknown: {
    platform: 'your deck builder',
    steps: [
      'Open your deck in the app',
      'Look for an "Export" or "Share" option',
      'Export as text/Arena format',
      'Copy and paste the decklist here',
    ],
  },
}
